import CompressIcon from "@mui/icons-material/Compress";
import ExpandIcon from "@mui/icons-material/Expand";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Captain } from "../../../types/Captain";
import {
    activeSliderItem,
    avatar,
    container,
    detailsInfo,
    sliderButton,
    sliderItem,
    sliderParent,
} from "./positionSliderStyles";

interface PositionSliderProps {
    captains: Captain[];
    currentIndex: number;
    onScrollToIndex: (index: number) => void;
}

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const PositionSlider: React.FC<PositionSliderProps> = ({
    captains,
    currentIndex,
    onScrollToIndex,
}) => {
    const [showSlider, setShowSlider] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);

    const getItemSliderStyles = useMemo(() => {
        return captains.map((_, index) => {
            const styles = { ...sliderItem };

            if (index === currentIndex) {
                return { ...styles, ...activeSliderItem };
            }

            return styles;
        });
    }, [captains, currentIndex]);

    const handleClick = (index: number) => {
        onScrollToIndex(index);

        scrollToSelectedItem(index);
    };

    const scrollToSelectedItem = (index: number) => {
        const sliderElement = sliderRef.current;
        const selectedItem = sliderElement?.children[index] as HTMLElement;

        if (sliderElement && selectedItem) {
            const containerHeight = sliderElement.clientHeight;
            const itemOffsetTop = selectedItem.offsetTop;
            const itemHeight = selectedItem.clientHeight;

            const scrollToPosition =
                itemOffsetTop - containerHeight / 2 + itemHeight / 2;
            sliderElement.scrollTo({
                top: scrollToPosition,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        scrollToSelectedItem(currentIndex);
    }, [currentIndex]);

    return (
        <Box sx={container}>
            <IconButton
                onClick={() => setShowSlider((prev) => !prev)}
                sx={sliderButton}
            >
                {showSlider ? <CompressIcon /> : <ExpandIcon />}
            </IconButton>

            <Box ref={sliderRef} sx={sliderParent(isMobile)}>
                {showSlider &&
                    captains.map((captain, index) => (
                        <Box
                            key={index}
                            sx={getItemSliderStyles[index]}
                            onClick={() => handleClick(index)}
                            onMouseEnter={() =>
                                !isMobile && setShowDetails(true)
                            }
                            onMouseLeave={() =>
                                !isMobile && setShowDetails(false)
                            }
                        >
                            <Box
                                component="img"
                                sx={avatar}
                                src={captain.imageUrl}
                                alt={`${captain.firstName} ${captain.lastName}`}
                                loading="lazy"
                            />

                            {showDetails && (
                                <Box sx={detailsInfo}>
                                    <Typography
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        {captain.firstName[0]}.{" "}
                                        {captain.lastName}
                                    </Typography>
                                    <Typography variant="caption">
                                        {captain.yearsAsCaptain}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ))}
            </Box>
        </Box>
    );
};

export default PositionSlider;
